import { create } from 'zustand';
import { Product } from '@/lib/constants';
import { productsApi } from '@/lib/api';
import { logger } from '@/lib/logger';

interface ProductsState {
  products: Product[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchProducts: () => Promise<void>;
  addProduct: (product: Omit<Product, 'id' | 'vendor_id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProduct: (productId: string, updates: Partial<Product>) => Promise<void>;
  deleteProduct: (productId: string) => Promise<void>;
  toggleProduct: (productId: string) => Promise<void>;
  updateStock: (productId: string, stock_status: string) => Promise<void>;
  updateProductImages: (productId: string, primaryImageId: string | null, galleryImageIds: string[]) => Promise<void>; // Sprint 4I
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

export const useProductsStore = create<ProductsState>((set) => ({
  products: [],
  isLoading: false,
  error: null,

  fetchProducts: async () => {
    try {
      set({ isLoading: true, error: null });
      const products = await productsApi.getProducts();
      set({ products, isLoading: false });
    } catch (error) {
      logger.error('Failed to fetch products', error as Error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  addProduct: async (product) => {
    try {
      set({ isLoading: true, error: null });
      const newProduct = await productsApi.createProduct(product as any);
      set((state) => ({
        products: [newProduct, ...state.products],
        isLoading: false,
      }));
      logger.info('Product added successfully', { productId: newProduct.id });
    } catch (error) {
      logger.error('Failed to add product', error as Error);
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  updateProduct: async (productId, updates) => {
    try {
      set({ isLoading: true, error: null });
      const updatedProduct = await productsApi.updateProduct(productId, updates as any);
      
      set((state) => ({
        products: state.products.map((p) =>
          p.id === productId ? updatedProduct : p
        ),
        isLoading: false,
      }));
      
      logger.info('Product updated successfully', { productId });
    } catch (error) {
      logger.error('Failed to update product', error as Error, { productId });
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  deleteProduct: async (productId) => {
    try {
      set({ isLoading: true, error: null });
      await productsApi.deleteProduct(productId);
      
      set((state) => ({
        products: state.products.filter((p) => p.id !== productId),
        isLoading: false,
      }));
      
      logger.info('Product deleted successfully', { productId });
    } catch (error) {
      logger.error('Failed to delete product', error as Error, { productId });
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  toggleProduct: async (productId) => {
    try {
      const updatedProduct = await productsApi.toggleProduct(productId);
      
      set((state) => ({
        products: state.products.map((p) =>
          p.id === productId ? updatedProduct : p
        ),
      }));
      
      logger.info('Product toggled successfully', { productId, isActive: updatedProduct.is_active });
    } catch (error) {
      logger.error('Failed to toggle product', error as Error, { productId });
      throw error;
    }
  },

  updateStock: async (productId, stock_status) => {
    try {
      const updatedProduct = await productsApi.updateStock(productId, stock_status);
      
      set((state) => ({
        products: state.products.map((p) =>
          p.id === productId ? updatedProduct : p
        ),
      }));
      
      logger.info('Product stock updated successfully', { productId, stock_status });
    } catch (error) {
      logger.error('Failed to update product stock', error as Error, { productId });
      throw error;
    }
  },

  // Sprint 4I: Update product images
  updateProductImages: async (productId, primaryImageId, galleryImageIds) => {
    try {
      const updatedProduct = await productsApi.updateProduct(productId, {
        primary_image_id: primaryImageId,
        gallery_image_ids: galleryImageIds,
      });
      
      set((state) => ({
        products: state.products.map((p) =>
          p.id === productId ? updatedProduct : p
        ),
      }));
      
      logger.info('Product images updated successfully', { productId, primaryImageId, galleryCount: galleryImageIds.length });
    } catch (error) {
      logger.error('Failed to update product images', error as Error, { productId });
      throw error;
    }
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setError: (error) => {
    set({ error });
  },

  reset: () => {
    set({
      products: [],
      isLoading: false,
      error: null,
    });
  },
}));









